// src/pages/SendPackagePage/MyDeliveriesList.jsx
import React, { useState } from "react";
import {
  Box,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Stack,
  Button,
} from "@mui/material";
import { CreditCard, RotateCcw, FileText, PackageCheck, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PaymentModal } from "./Payment/PaymentModal";
import { DeliverySummaryModal } from "../../shared/components/Modals/DeliverySummaryModal";
import { DeliveryDetailsModal } from "../../shared/components/Modals/DeliveryDetailsModal";
import { BusTransportModal } from "../../shared/components/Modals/BusTransportModal/BusTransportModal";
import {
  EditDeliveryRequestModal,
  canSenderEdit,
} from "../../shared/components/Modals/EditDeliveryRequestModal";
import { shiftDeliveryTimes } from "../../utils/timeUtils";

export const MyDeliveriesList = ({
  deliveries,
  highlightDeliveryId,
  handlePrintWaybill,
  onResendConfirm,
  onDeliveryUpdated,
}) => {
  const { t } = useTranslation();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsDelivery, setDetailsDelivery] = useState(null);
  const [busTransportModalOpen, setBusTransportModalOpen] = useState(false);
  const [resendData, setResendData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editDelivery, setEditDelivery] = useState(null);

  // ── Data model helpers ───────────────────────────────────────────────────

  const getDeliveryId = (d) => d?.id || d?.deliveryId;

  const getPickupTime = (d) => d?.pickupTime ?? d?.pickUpTime ?? d?.createdAtMs;
  const getTargetTime = (d) => d?.targetTime ?? d?.createdAtMs;

  const getFromAddress = (d) =>
    d?.originInfo?.address ??
    (typeof d?.origin === "string" ? d.origin : null) ??
    "—";

  const getToAddress = (d) =>
    d?.destinationInfo?.address ??
    (typeof d?.destination === "string" ? d.destination : null) ??
    "—";

  const getFromDetails = (d) => d?.originInfo?.details || {};
  const getToDetails = (d) => d?.destinationInfo?.details || {};

  const getSenderName = (d) => d?.senderInfo?.name || "";
  const getSenderPhone = (d) => d?.senderPhone || "";
  const getSenderEmail = (d) => d?.senderInfo?.email || "";
  const getRecipientName = (d) => d?.receiverInfo?.name || "";
  const getRecipientPhone = (d) => d?.receiverPhone || "";
  const getRecipientEmail = (d) => d?.receiverInfo?.email || "";

  // ── Guards ───────────────────────────────────────────────────────────────

  if (!deliveries || deliveries.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        {t("delivery.noDeliveries") || "No deliveries yet"}
      </Alert>
    );
  }

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleRowClick = (delivery, event) => {
    if (
      event.target.closest('input[type="checkbox"]') ||
      event.target.closest("button")
    )
      return;
    setDetailsDelivery(delivery);
    setDetailsModalOpen(true);
  };

  const handleSelectAll = (event) => {
    setSelected(event.target.checked ? deliveries.map(getDeliveryId) : []);
  };

  const handleSelectOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handlePrintSelected = () => {
    deliveries
      .filter((d) => selected.includes(getDeliveryId(d)))
      .forEach((d) => handlePrintWaybill(d));
  };

  const isSelected = (id) => selected.includes(id);
  const isAllSelected =
    deliveries.length > 0 && selected.length === deliveries.length;
  const isSomeSelected =
    selected.length > 0 && selected.length < deliveries.length;

  const getStatusColor = (status) =>
    ({
      active: "warning",
      ongoing: "info",
      accepted: "info",
      picked: "primary",
      completed: "success",
      cancelled: "error",
    }[status] || "default");

  const getStatusLabel = (status) =>
    ({
      active: t("delivery.status.active") || "Active",
      ongoing: t("delivery.status.ongoing") || "Ongoing",
      accepted: t("delivery.status.accepted") || "Accepted",
      picked: t("delivery.status.picked") || "Picked Up",
      completed: t("delivery.status.completed") || "Completed",
      cancelled: t("delivery.status.cancelled") || "Cancelled",
    }[status] || status);

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return "—";
    return date
      .toLocaleString("fi-FI", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(",", "");
  };

  const canPrintWaybill = (d) =>
    d.status !== "completed" && d.status !== "cancelled";

  const isCardPaymentDelivery = (delivery) =>
    delivery?.billing?.paymentMethod === "card" ||
    delivery?.paymentMethod === "card";

  const isUnpaidCardDelivery = (delivery) =>
    isCardPaymentDelivery(delivery) && delivery?.paid !== true;

  const handlePayClick = (delivery, event) => {
    event.stopPropagation();
    setSelectedDelivery(delivery);
    setPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    setPaymentModalOpen(false);
    setSelectedDelivery(null);
  };

  const handleEditClick = (delivery) => {
    setEditDelivery(delivery);
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setEditDelivery(null);
  };

  const handleResendClick = (delivery) => {
    const shiftedTimes = shiftDeliveryTimes(delivery);
    const fromDetails = getFromDetails(delivery);
    const toDetails = getToDetails(delivery);

    const data = {
      ...delivery,
      ...shiftedTimes,

      origin: getFromAddress(delivery),
      destination: getToAddress(delivery),

      originData: {
        latitude: delivery.originInfo?.latitude,
        longitude: delivery.originInfo?.longitude,
        address: delivery.originInfo?.address,
        placeId: delivery.originInfo?.placeId,
        city: delivery.originInfo?.city,
        postalCode: delivery.originInfo?.postalCode,
      },
      destData: {
        latitude: delivery.destinationInfo?.latitude,
        longitude: delivery.destinationInfo?.longitude,
        address: delivery.destinationInfo?.address,
        placeId: delivery.destinationInfo?.placeId,
        city: delivery.destinationInfo?.city,
        postalCode: delivery.destinationInfo?.postalCode,
      },

      pickupApartment: fromDetails.apartment || "",
      pickupFloor: fromDetails.floor || "",
      pickupBuilding: fromDetails.building || "",
      pickupAccessCode: fromDetails.accessCode || "",
      pickupInstructions: fromDetails.instructions || "",

      senderName: getSenderName(delivery),
      senderPhone: getSenderPhone(delivery),
      senderEmail: getSenderEmail(delivery),

      deliveryApartment: toDetails.apartment || "",
      deliveryFloor: toDetails.floor || "",
      deliveryBuilding: toDetails.building || "",
      deliveryAccessCode: toDetails.accessCode || "",
      deliveryInstructions: toDetails.instructions || "",

      recipientName: getRecipientName(delivery),
      recipientPhone: getRecipientPhone(delivery),
      recipientEmail: getRecipientEmail(delivery),

      paymentMethod: delivery.billing?.paymentMethod || "invoice",
      yNumber: delivery.billing?.yNumber || "",
      companyName: delivery.billing?.companyName || "",
      paymentReference: delivery.billing?.paymentReference || "",
      billingDepartment: delivery.billing?.billingDepartment || "",

      returnParcels: delivery.returnParcels || false,
      returnMethod: delivery.returnMethod || "bus",
      returnLink: delivery.returnLink || "",
      temperatureControl: delivery.temperatureControl || false,
      canBeLeftAtDoor: delivery.canBeLeftAtDoor || false,
      busTransportData: delivery.busTransportData || null,

      estimatedPrice: delivery.price || 0,
      timeWindowFee: delivery.timeWindowFee || 0,
      totalPrice: (delivery.price || 0) + (delivery.timeWindowFee || 0),
      selectedTransportLabel: delivery.deliveryTransportType || "",
    };
    console.log("data ------>", data);
    setResendData(data);
    setResendModalOpen(true);
  };

  const handleResendConfirm = () => {
    if (onResendConfirm) onResendConfirm(resendData);
    setResendModalOpen(false);
  };

  const handleCloseResend = () => {
    setResendModalOpen(false);
    setResendData(null);
  };

  const updateResendData = (field, value) => {
    setResendData((prev) => ({ ...prev, [field]: value }));
  };

  // Accepts (price, busData, departureMs, arrivalMs) — update times alongside bus data
  const handleBusTransportConfirm = (
    price,
    busData,
    departureMs,
    arrivalMs
  ) => {
    setResendData((prev) => ({
      ...prev,
      busTransportData: busData,
      estimatedPrice: price,
      totalPrice: price + (prev?.timeWindowFee || 0),
      ...(departureMs && { pickupTime: departureMs }),
      ...(arrivalMs && { targetTime: arrivalMs }),
    }));
    setBusTransportModalOpen(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {selected.length > 0 && (
        <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {selected.length} {t("delivery.selected") || "selected"}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileText size={16} />}
            onClick={handlePrintSelected}
          >
            {t("delivery.printSelected") || "Print Selected Waybills"}
          </Button>
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>{t("deliveryPickUpTime") || "Pickup Time"}</TableCell>
              <TableCell>{t("deliveryTargetTime") || "Target Time"}</TableCell>
              <TableCell>{t("from") || "From"}</TableCell>
              <TableCell>{t("to") || "To"}</TableCell>
              <TableCell>{t("status") || "Status"}</TableCell>
              <TableCell align="right">{t("actions") || "Actions"}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {[...deliveries]
              .sort((a, b) => (getPickupTime(b) ?? 0) - (getPickupTime(a) ?? 0))
              .map((delivery) => {
                const deliveryId = getDeliveryId(delivery);
                const isItemSelected = isSelected(deliveryId);

                return (
                  <TableRow
                    key={deliveryId}
                    hover
                    selected={isItemSelected}
                    onClick={(e) => handleRowClick(delivery, e)}
                    sx={{
                      cursor: "pointer",
                      bgcolor:
                        highlightDeliveryId === deliveryId
                          ? "action.selected"
                          : "inherit",
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onChange={() => handleSelectOne(deliveryId)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(getPickupTime(delivery))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(getTargetTime(delivery))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                        {getFromAddress(delivery)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                        {getToAddress(delivery)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                       
                        {isUnpaidCardDelivery(delivery) ? 
                          <Chip
                            size="small"
                            label={t("delivery.paymentRequired") || "Payment required"}
                            color="warning"
                                                          onClick={(event) => handlePayClick(delivery, event)}

                            variant="outlined"
                          />
                        :  <Chip
                          size="small"
                          label={getStatusLabel(delivery.status || "active")}
                          color={getStatusColor(delivery.status || "active")}
                        />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        {canSenderEdit(delivery) && (
                          <Tooltip
                            title={"Muokkaa"}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(delivery)}
                            >
                              <Pencil size={16} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {canPrintWaybill(delivery) && (
                          <Tooltip
                            title={
                              t("delivery.printWaybill") || "Print Waybill"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => handlePrintWaybill(delivery)}
                            >
                              <FileText size={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {delivery.returnLink && (
                          <Tooltip title={"Paluulinkki"}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() =>
                                window.open(delivery.returnLink, "_blank")
                              }
                            >
                              <PackageCheck size={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t("delivery.resend") || "Resend"}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleResendClick(delivery)}
                          >
                            <RotateCcw size={16} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedDelivery && (
        <PaymentModal
          open={paymentModalOpen}
          amount={selectedDelivery?.price || 1}
          onSuccess={handlePaymentSuccess}
          onClose={() => {
            setPaymentModalOpen(false);
            setSelectedDelivery(null);
          }}
          title={t("delivery.payForDelivery") || "Pay for Delivery"}
          metadata={{
            deliveryId: getDeliveryId(selectedDelivery),
            deliveryType: selectedDelivery.deliveryTransportType,
            from: getFromAddress(selectedDelivery),
            to: getToAddress(selectedDelivery),
          }}
        />
      )}

      {resendData && (
        <DeliverySummaryModal
          open={resendModalOpen}
          onClose={handleCloseResend}
          onConfirm={handleResendConfirm} // ← receives final merged data
          delivery={resendData}
          onEditBusOption={() => setBusTransportModalOpen(true)}
          estimatedPrice={resendData.estimatedPrice}
          timeWindowFee={resendData.timeWindowFee}
          totalPrice={resendData.totalPrice}
          selectedTransportLabel={resendData.selectedTransportLabel}
          onUpdateField={updateResendData}
        />
      )}

      {resendData && (
        <BusTransportModal
          open={busTransportModalOpen}
          onClose={() => setBusTransportModalOpen(false)}
          origin={resendData.origin}
          destination={resendData.destination}
          pickupTime={resendData.pickupTime}
          onConfirm={handleBusTransportConfirm}
        />
      )}

      {editDelivery && (
        <EditDeliveryRequestModal
          open={editModalOpen}
          delivery={editDelivery}
          onClose={handleCloseEdit}
          onSaved={() => onDeliveryUpdated?.()}
        />
      )}

      <DeliveryDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        delivery={detailsDelivery}
      />
    </>
  );
};
